import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Button,
  FormControl, InputLabel, Select, MenuItem, TextField,
  CircularProgress, Alert, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import exportacaoService from '../services/exportacaoService';

interface Departamento {
  id: number;
  nome: string;
}

const RelatorioTemporizadorPage: React.FC = () => {
  const { usuario } = useAuth();
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [departamentoId, setDepartamentoId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [status, setStatus] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  useEffect(() => {
    api.get('/departamentos').then((res) => {
      setDepartamentos(res.data.departamentos || res.data || []);
    });
  }, []);

  const isAdmin = usuario?.tipo === 'admin';
  const isLider = usuario?.eh_lider;

  if (!isAdmin && !isLider) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">Acesso restrito a administradores e líderes.</Alert>
      </Container>
    );
  }

  const handleExportar = async () => {
    setErro('');
    setSucesso('');
    setCarregando(true);
    try {
      const params: Record<string, string> = {};
      if (departamentoId) params.departamentoId = departamentoId;
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
      if (status) params.status = status;

      const res = await api.get('/relatorios/temporizador', { params });
      const { tarefas, tempoResponsavel, etapas } = res.data;

      if (!tarefas?.length) {
        setErro('Nenhuma tarefa encontrada com os filtros informados.');
        return;
      }

      const deptoNome = departamentos.find((d) => String(d.id) === departamentoId)?.nome;

      exportacaoService.exportarRelatorioTemporizador({
        tarefas,
        tempoResponsavel,
        etapas,
        filtros: { departamento: deptoNome, dataInicio, dataFim, status }
      });

      setSucesso(`Relatório exportado com sucesso — ${tarefas.length} tarefa(s) encontrada(s).`);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro desconhecido';
      setErro(`Erro: ${msg}`);
      console.error('Erro ao gerar relatório temporizador:', err);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 3, mt: 4, mb: 6 }}>

      {/* Cabeçalho */}
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: 'primary.main', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <AccessTimeIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
            Relatório do Temporizador
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Exporte dados de tempo registrados nos cards de tarefas
          </Typography>
        </Box>
      </Box>

      {/* Card de filtros */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 3 }}>

        <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', borderRadius: '12px 12px 0 0' }}>
          <Typography variant="subtitle1" fontWeight={600}>Filtros</Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Linha 1: Departamento e Status */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormControl fullWidth>
                <InputLabel id="dep-label" shrink>Departamento</InputLabel>
                <Select
                  labelId="dep-label"
                  value={departamentoId}
                  label="Departamento"
                  displayEmpty
                  notched
                  onChange={(e) => setDepartamentoId(e.target.value)}
                >
                  <MenuItem value="">Todos os departamentos</MenuItem>
                  {departamentos.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>{d.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormControl fullWidth>
                <InputLabel id="status-label" shrink>Status da Tarefa</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status da Tarefa"
                  displayEmpty
                  notched
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="">Todos os status</MenuItem>
                  <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="Concluída">Concluída (inclui Finalizada)</MenuItem>
                  <MenuItem value="concluida_com_atraso">Concluídas com Atraso</MenuItem>
                  <MenuItem value="Cancelada">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Linha 2: Datas */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Data de Início"
                type="date"
                fullWidth
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Data de Fim"
                type="date"
                fullWidth
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
          {sucesso && <Alert severity="success" sx={{ mt: 2 }}>{sucesso}</Alert>}

          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="large"
              startIcon={carregando ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportar}
              disabled={carregando}
              sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 600, minWidth: 240 }}
            >
              {carregando ? 'Gerando relatório...' : 'Gerar e Exportar (.xlsx)'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Conteúdo do arquivo */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={2.5} textTransform="uppercase" letterSpacing={0.5} fontSize={11}>
          Conteúdo do arquivo exportado
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box display="flex" gap={1.5} alignItems="flex-start">
            <TableChartIcon sx={{ color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Aba 1 — Resumo por Tarefa</Typography>
              <Typography variant="body2" color="text.secondary">
                ID, título, departamento, status, prioridade, datas, tempo total acumulado e participantes
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} alignItems="flex-start">
            <PeopleIcon sx={{ color: 'success.main', mt: 0.2, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Aba 2 — Tempo por Responsável</Typography>
              <Typography variant="body2" color="text.secondary">
                Cada sessão do temporizador com responsável, início, fim e duração
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} alignItems="flex-start">
            <HistoryIcon sx={{ color: 'warning.main', mt: 0.2, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Aba 3 — Histórico de Etapas</Typography>
              <Typography variant="body2" color="text.secondary">
                Mudanças de status com data/hora e nome do responsável pela alteração
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

    </Box>
  );
};

export default RelatorioTemporizadorPage;
